export type AdminRefusal = { status: 'PENDING' | 'BANK_REVIEW' | 'REFUSED'; reason: string };
export type CommentReview = {
  commentVersion: string;
  status: 'PENDING' | 'APPROVED' | 'DECLINED';
  providerId?: string;
  reason?: string;
  checkedAt?: string;
};
export const commentBlocks = (request: {
  travelerComment?: string;
  travelerCommentReview?: CommentReview | null;
}) =>
  Boolean(request.travelerComment?.trim()) &&
  request.travelerCommentReview?.status !== 'APPROVED';

export type Cancellation = {
  id: string;
  status:
    | 'REQUESTED'
    | 'CHECKED'
    | 'CONTROL_FAILED'
    | 'REJECTED'
    | 'REFUNDED_SIMULATED'
    | 'REFUND_PENDING'
    | 'REFUND_FAILED'
    | 'REFUNDED'
    | 'REFUND_REVIEW'
    | 'SETTLEMENT_PENDING'
    | 'SETTLED';
  refundAmountMinor?: number | null;
  requestedAt: string;
  checkedBy?: string | null;
  checkedAt?: string | null;
  notDelivered?: boolean | null;
  notConsumed?: boolean | null;
  reason: string | null;
};
export const cancellationLabel = (
  c: Pick<Cancellation, 'status' | 'refundAmountMinor'>,
): string =>
  ({
    SETTLEMENT_PENDING: 'Annulation en cours · traitement bancaire à vérifier',
    SETTLED:
      (c.refundAmountMinor ?? 0) > 0
        ? 'Commande annulée · remboursement confirmé'
        : 'Commande annulée · empreinte libérée, aucun prélèvement',
    REQUESTED: 'Annulation demandée · contrôle à faire',
    CHECKED: 'Contrôle favorable · décision admin attendue',
    CONTROL_FAILED: 'Contrôle défavorable · décision admin attendue',
    REJECTED: 'Annulation refusée',
    REFUNDED_SIMULATED: 'Commande annulée · remboursement simulé',
    REFUND_PENDING: 'Remboursement en cours · commande suspendue',
    REFUND_FAILED: 'Remboursement échoué · intervention administrateur requise',
    REFUNDED: 'Commande annulée · remboursement confirmé',
    REFUND_REVIEW: 'Remboursement à vérifier avec l’administrateur',
  })[c.status];
export const cancellationBlocks = (request: {
  adminRefusal?: AdminRefusal;
  cancellation?: Cancellation;
  modifications?: Modification[];
}): boolean =>
  Boolean(request.adminRefusal) ||
  Boolean(request.cancellation && request.cancellation.status !== 'REJECTED') ||
  modificationBlocks(request);

export type OrderReceipt = {
  deliveryRequestId: string;
  credential: string;
  travelerName?: string;
};
const travelerKey = 'otem-active-traveler-name-v1';
export const normalizeTravelerName = (value: string) =>
  value.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('fr-FR');
export function activeTravelerName(): string {
  try {
    return globalThis.sessionStorage.getItem(travelerKey) ?? '';
  } catch {
    return '';
  }
}
export function selectTravelerName(name: string): void {
  try {
    globalThis.sessionStorage.setItem(travelerKey, name.trim());
  } catch {
    /* No persistence required to use the current view. */
  }
}

const key = 'otem-private-order-receipts-v1';
export function readOrderReceipts(): OrderReceipt[] {
  try {
    const values: unknown = JSON.parse(globalThis.localStorage.getItem(key) ?? '[]');
    return Array.isArray(values)
      ? values.filter(
          (v) => typeof v?.deliveryRequestId === 'string' && typeof v?.credential === 'string',
        )
      : [];
  } catch {
    return [];
  }
}
export function saveOrderReceipt(receipt: OrderReceipt): void {
  if (receipt.travelerName?.trim()) selectTravelerName(receipt.travelerName);
  const receipts = readOrderReceipts().filter(
    (r) => r.deliveryRequestId !== receipt.deliveryRequestId,
  );
  globalThis.localStorage.setItem(key, JSON.stringify([...receipts, receipt]));
}

export type Modification = {
  id: string;
  status:
    | 'REQUESTED'
    | 'CHECKED'
    | 'CONTROL_FAILED'
    | 'APPLIED'
    | 'REJECTED'
    | 'REFUND_PENDING'
    | 'REFUND_FAILED'
    | 'REFUND_REVIEW'
    | 'PAYMENT_PENDING';
  from: { displayName: string; amountMinor?: number };
  to: { displayName: string; amountMinor?: number };
  deltaMinor?: number;
  checkedAt?: string | null;
  checkedBy?: string | null;
  reason?: string | null;
  settlement?: { kind: string; amountMinor: number } | null;
  paymentBeforeControl?: boolean;
  capturedAmountMinor?: number;
  refundedAmountMinor?: number;
  paymentRequired?: boolean;
  controlAvailable?: boolean;
  withdrawalRequested?: boolean;
  paymentStatus?: string;
  captureMethod?: 'manual' | 'automatic' | null;
  bankOperationPending?: boolean;
  bankOperationFailed?: boolean;
};
export const modificationBlocks = (r: { modifications?: Modification[] }): boolean =>
  Boolean(r.modifications?.some((m) => !['APPLIED', 'REJECTED'].includes(m.status)));
export const modificationLabel = (m: Modification): string =>
  m.bankOperationFailed
    ? 'Opération bancaire à vérifier · commande suspendue'
    : m.bankOperationPending
      ? 'Traitement bancaire en cours · commande suspendue'
      : m.withdrawalRequested && m.status !== 'REJECTED'
        ? 'Abandon demandé · contrôle préparateur et décision admin attendus'
        : m.paymentRequired
          ? 'Complément à régler avant le contrôle préparateur'
          : m.paymentBeforeControl &&
              m.status === 'REQUESTED' &&
              m.paymentStatus === 'AUTHORIZED'
            ? 'Complément réservé · faisabilité à confirmer'
            : {
                REQUESTED: 'Modification demandée · contrôle préparateur requis',
                CHECKED:
                  (m.deltaMinor ?? 0) > 0
                    ? 'Complément à payer'
                    : 'Validation administrateur attendue',
                CONTROL_FAILED: 'Contrôle défavorable · décision administrateur requise',
                APPLIED: 'Modification confirmée',
                PAYMENT_PENDING: 'Complément en attente de paiement',
                REFUND_REVIEW: 'Remboursement à vérifier avec l’administrateur',
                REFUND_PENDING: 'Remboursement en cours · modification suspendue',
                REFUND_FAILED: 'Remboursement échoué · intervention administrateur requise',
                REJECTED: 'Modification non retenue',
              }[m.status];

export type OrderFunding = {
  policy: string;
  reservedAmountMinor: number;
  capturedAmountMinor: number;
  refundedAmountMinor: number;
  bankOperationPending: boolean;
  bankReviewRequired?: boolean;
  paymentStatus: string;
};
